import React, { useMemo } from "react";
import { type NextPage } from "next";
import { Page } from "components/page";
import { getLandingPageProps, getPageMetaImage, type LandingPageProps } from "utils";
import { FeaturedCaseStudyHero } from "components/case-studies/featured-case-study-hero";
import { handleClientError } from "lib/errors";
import { Section, sectionSpacingY } from "components/section";
import { TextTitle } from "ui/text";
import { CaseStudiesCatalog } from "components/case-studies/case-studies-catalog";
import { compact } from "lodash";
import { FeaturedCaseStudiesSection } from "components/case-studies/featured-case-studies-section";

const heroCaseStudySlug = "wmg";
const featuredCaseStudySlugs = ["petsmart", "weight-watchers", "chime", "whoop"];

const Customers: NextPage<LandingPageProps> = ({ page, announcementBanner, defaultLogos, caseStudies }) => {
  const heroCaseStudy = useMemo(() => {
    return caseStudies.find((caseStudy) => caseStudy.customer.slug === heroCaseStudySlug);
  }, [caseStudies]);
  const featuredCaseStudies = compact(
    featuredCaseStudySlugs.map((slug) => caseStudies.find((caseStudy) => caseStudy.customer.slug === slug)),
  );

  if (!heroCaseStudy || featuredCaseStudies.length !== featuredCaseStudySlugs.length) {
    handleClientError(
      "One of the expected featured case studies on the customer stories page was not found in Sanity.",
    );
  }

  return (
    <Page
      theme="dark"
      bgColor="new.violet.950"
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      footerOptions={{ variant: "violet" }}
    >
      <FeaturedCaseStudyHero
        title={page.title}
        description={page.description}
        caseStudy={heroCaseStudy}
        defaultLogos={defaultLogos}
      />

      <FeaturedCaseStudiesSection caseStudies={featuredCaseStudies} />

      <Section my="0" pb={sectionSpacingY}>
        <TextTitle
          size="lg"
          align="center"
          title="Customer stories"
          description="Hear more stories of teams across industries using Hightouch to securely sync their data."
          mb={{ base: "bigger", md: "biggest" }}
          maxW="lg"
        />

        <CaseStudiesCatalog caseStudies={caseStudies} />
      </Section>
    </Page>
  );
};

export default Customers;

export const getStaticProps = getLandingPageProps("/customers", { loadDefaultLogos: true, loadCaseStudies: "all" });
